import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  lazyRouteComponent,
  createHashHistory,
} from "@tanstack/react-router";
import { AppShell } from "./components/layout/app-shell.tsx";
import { PortalShell } from "./components/layout/portal-shell.tsx";
import { FullScreenLoader } from "./components/full-screen-loader.tsx";
import {
  demoLoginConfigured,
  demoSignIn,
  isSignedIn,
  loginPreviewEnabled,
  loginPreviewRequested,
} from "./lib/auth.ts";
import { api } from "./lib/api.ts";
import { currentAccountRole } from "./lib/auth.ts";
import { keys, queryClient } from "./lib/query.ts";
import { Skeleton } from "@dd/ui";

function RoutePending() {
  return (
    <div aria-label="Cargando pantalla" className="space-y-5">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-24 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-xl" />
    </div>
  );
}

function organizationStatus() {
  const cached = queryClient.getQueryData<
    Awaited<ReturnType<typeof api.organization.status>>
  >(keys.organization);
  return (
    cached ??
    queryClient.fetchQuery({
      queryKey: keys.organization,
      queryFn: api.organization.status,
    })
  );
}

/**
 * A qué panel entra la cuenta que tiene la sesión abierta.
 *
 * Un solo lugar decide esto. Repartido por cada `redirect`, alcanzaba con
 * olvidarse en uno para que una paciente cayera en el panel del consultorio
 * —que no puede usar— o al revés.
 */
function homeForCurrentAccount(): "/" | "/portal" {
  return currentAccountRole() === "paciente" ? "/portal" : "/";
}

/**
 * Routes are declared in code rather than generated from the filesystem — the
 * app is small enough that one readable tree beats a build step.
 */
const rootRoute = createRootRoute({ component: Outlet });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: lazyRouteComponent(
    () => import("./routes/login.tsx"),
    "LoginPage",
  ),
  pendingComponent: FullScreenLoader,
  /** `?ver=1` pide ver la pantalla; se valida para que sobreviva al recargado. */
  validateSearch: (search: Record<string, unknown>): { ver?: "1" } =>
    search.ver === "1" ? { ver: "1" } : {},
  beforeLoad: async () => {
    // Una sesión válida siempre gana. `?ver=1` sólo evita el ingreso automático
    // de la demo cuando todavía no hay sesión; nunca debe convertir el login en
    // una pantalla accesible desde adentro de la plataforma.
    if (isSignedIn()) throw redirect({ to: homeForCurrentAccount() });
    if (loginPreviewRequested()) return;
    // Con el ingreso automático puesto, llegar acá es un accidente: una sesión
    // que venció, un token viejo, un 401 en medio del uso. Se pide una nueva y
    // se vuelve al panel en vez de mostrar un formulario que la demo no tiene
    // por qué contestar.
    if (demoLoginConfigured() && (await demoSignIn())) {
      throw redirect({ to: "/" });
    }
  },
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: lazyRouteComponent(
    () => import("./routes/login.tsx"),
    "SignupPage",
  ),
  pendingComponent: FullScreenLoader,
  validateSearch: (search: Record<string, unknown>): { ver?: "1" } =>
    search.ver === "1" ? { ver: "1" } : {},
  beforeLoad: () => {
    if (isSignedIn()) throw redirect({ to: homeForCurrentAccount() });
    if (loginPreviewEnabled()) return;
  },
});

/**
 * El ingreso con el discurso de paciente ya puesto.
 *
 * Es el enlace que el consultorio reparte. Sin él, la primera visita de una
 * paciente a este navegador vería el texto del consultorio, que no le habla a
 * ella. Comparte pantalla y comportamiento con `/login`: lo único distinto es
 * a quién se dirige.
 */
const patientLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/paciente",
  component: lazyRouteComponent(
    () => import("./routes/login.tsx"),
    "PatientLoginPage",
  ),
  pendingComponent: FullScreenLoader,
  validateSearch: (search: Record<string, unknown>): { ver?: "1" } =>
    search.ver === "1" ? { ver: "1" } : {},
  beforeLoad: () => {
    if (isSignedIn()) throw redirect({ to: homeForCurrentAccount() });
    if (loginPreviewRequested()) return;
  },
});

/**
 * El enlace que el consultorio le manda a una paciente.
 *
 * Fuera del portal a propósito: quien lo abre puede no tener cuenta todavía, y
 * el portal exige sesión de paciente. Acá se ve quién invita y recién después
 * se pide entrar.
 */
const portalInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/paciente/invitacion/$token",
  component: lazyRouteComponent(
    () => import("./routes/portal-invite.tsx"),
    "PortalInvitePage",
  ),
  pendingComponent: FullScreenLoader,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: lazyRouteComponent(
    () => import("./routes/reset-password.tsx"),
    "ResetPasswordPage",
  ),
  pendingComponent: FullScreenLoader,
});

const statusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/status",
  component: lazyRouteComponent(
    () => import("./routes/status.tsx"),
    "StatusPage",
  ),
  pendingComponent: FullScreenLoader,
});

/**
 * Pathless layout route: everything under it renders inside the shell and is
 * gated behind a session. The check is a UX guard only — the API authorises
 * every request on its own.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: AppShell,
  pendingComponent: FullScreenLoader,
  pendingMs: 0,
  pendingMinMs: 300,
  beforeLoad: async () => {
    if (!isSignedIn() && !(await demoSignIn())) {
      throw redirect({ to: "/login" });
    }
    // Una cuenta de paciente no tiene consultorio que gestionar: sin esto, la
    // siguiente línea la manda a dar de alta una organización.
    if (currentAccountRole() === "paciente") throw redirect({ to: "/portal" });
    const status = await organizationStatus();
    if (!status.setupComplete) throw redirect({ to: "/onboarding" });
  },
});

/**
 * El panel de pacientes, detrás de su propia puerta.
 *
 * Va aparte del `appRoute` y no colgado de él: ese exige una organización con
 * la puesta en marcha terminada, que es exactamente lo que una paciente no
 * tiene ni va a tener. Colgarlo ahí la mandaba derecho al alta de un
 * consultorio.
 *
 * El chequeo del rol es de comodidad, no de seguridad: evita que alguien
 * termine en el panel que no puede usar. Lo que cada cuenta puede leer de
 * verdad lo decide la API en cada pedido.
 */
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "portal",
  component: PortalShell,
  pendingComponent: FullScreenLoader,
  beforeLoad: () => {
    if (!isSignedIn()) throw redirect({ to: "/paciente" });
    if (currentAccountRole() !== "paciente") throw redirect({ to: "/" });
  },
});

const portalHomeRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: "/portal",
  component: lazyRouteComponent(
    () => import("./routes/portal.tsx"),
    "PortalHomePage",
  ),
});

/**
 * Una solapa por pantalla, como en el panel del consultorio.
 *
 * Todas leen la misma consulta —el panorama del inicio ya trae todo— así que
 * moverse entre ellas no vuelve a pedir nada.
 */
const portalAppointmentsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: "/portal/turnos",
  component: lazyRouteComponent(
    () => import("./routes/portal.tsx"),
    "PortalAppointmentsPage",
  ),
});
const portalOrdersRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: "/portal/estudios",
  component: lazyRouteComponent(
    () => import("./routes/portal.tsx"),
    "PortalOrdersPage",
  ),
});
const portalMedicationsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: "/portal/medicacion",
  component: lazyRouteComponent(
    () => import("./routes/portal.tsx"),
    "PortalMedicationsPage",
  ),
});
const portalInstructionsRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: "/portal/indicaciones",
  component: lazyRouteComponent(
    () => import("./routes/portal.tsx"),
    "PortalInstructionsPage",
  ),
});
/**
 * Una indicación sola, en su propia pantalla.
 *
 * Y no una ventana encima de la lista: una indicación es un texto para leer
 * entero —cuidados, señales de alarma, a quién escribir— y en el teléfono una
 * hoja que tapa media pantalla se lee peor que una página, que además se
 * comparte, se agranda y vuelve con el botón de atrás.
 */
const portalInstructionRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: "/portal/indicaciones/$instructionId",
  component: lazyRouteComponent(
    () => import("./routes/portal.tsx"),
    "PortalInstructionPage",
  ),
});

const portalWaitingRoomRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: "/portal/sala",
  component: lazyRouteComponent(
    () => import("./routes/portal.tsx"),
    "PortalWaitingRoomPage",
  ),
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: lazyRouteComponent(
    () => import("./routes/onboarding.tsx"),
    "OnboardingPage",
  ),
  pendingComponent: FullScreenLoader,
  beforeLoad: async () => {
    if (!isSignedIn()) throw redirect({ to: "/login" });
    if (currentAccountRole() === "paciente") throw redirect({ to: "/portal" });
    const status = await organizationStatus();
    if (status.setupComplete) throw redirect({ to: "/" });
  },
});

const overviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: lazyRouteComponent(
    () => import("./routes/overview.tsx"),
    "OverviewPage",
  ),
});

const patientsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/patients",
  /**
   * `?patient=` se conserva sólo para redirigir enlaces antiguos a la nueva
   * subpantalla `/patients/:id`.
   *
   * `?timeline=` guarda el filtro de la cronología y `?event=` el renglón al
   * que se quiere llegar. Van en la dirección por lo mismo que la ficha: una
   * vista filtrada que no se puede pasar por chat obliga a explicar de palabra
   * cómo llegar a ella.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    patient?: string;
    action?: "new" | "import";
    timeline?: string;
    event?: string;
  } => ({
    ...(typeof search.patient === "string" && search.patient
      ? { patient: search.patient }
      : {}),
    ...(search.action === "new" || search.action === "import"
      ? { action: search.action }
      : {}),
    ...(typeof search.timeline === "string" && search.timeline
      ? { timeline: search.timeline }
      : {}),
    ...(typeof search.event === "string" && search.event
      ? { event: search.event }
      : {}),
  }),
  beforeLoad: ({ search }) => {
    // Enlaces guardados de la antigua vista partida siguen llevando a la ficha
    // correcta mientras la URL canónica pasa a ser una subpantalla real.
    if (search.patient) {
      throw redirect({
        to: "/patients/$patientId",
        params: { patientId: search.patient },
        search: {
          ...(search.timeline ? { timeline: search.timeline } : {}),
          ...(search.event ? { event: search.event } : {}),
        },
        replace: true,
      });
    }
  },
  component: lazyRouteComponent(
    () => import("./routes/patients.tsx"),
    "PatientsPage",
  ),
});
const patientDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/patients/$patientId",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    view?:
      | "ficha"
      | "familia"
      | "seguridad"
      | "mediciones"
      | "estudios"
      | "documentos"
      | "consentimientos"
      | "fotos"
      | "citas"
      | "historial"
      | "comercial";
    timeline?: string;
    event?: string;
  } => ({
    ...([
      "ficha",
      "familia",
      "seguridad",
      "mediciones",
      "estudios",
      "documentos",
      "consentimientos",
      "fotos",
      "citas",
      "historial",
      "comercial",
    ].includes(String(search.view))
      ? { view: search.view as "ficha" }
      : {}),
    ...(typeof search.timeline === "string" && search.timeline
      ? { timeline: search.timeline }
      : {}),
    ...(typeof search.event === "string" && search.event
      ? { event: search.event }
      : {}),
  }),
  component: lazyRouteComponent(
    () => import("./routes/patients.tsx"),
    "PatientDetailPage",
  ),
});

const waitingRoomRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/waiting-room",
  /**
   * La vista y la sede van en la dirección.
   *
   * Un monitor colgado en la sala arranca solo en la pantalla correcta con un
   * enlace, sin que nadie tenga que configurarlo a mano cada mañana.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    view?: "reception" | "professional" | "display";
    locationId?: string;
    professionalId?: string;
  } => ({
    ...(search.view === "reception" ||
    search.view === "professional" ||
    search.view === "display"
      ? { view: search.view }
      : {}),
    ...(typeof search.locationId === "string" && search.locationId
      ? { locationId: search.locationId }
      : {}),
    ...(typeof search.professionalId === "string" && search.professionalId
      ? { professionalId: search.professionalId }
      : {}),
  }),
  component: lazyRouteComponent(
    () => import("./routes/waiting-room.tsx"),
    "WaitingRoomPage",
  ),
});

const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tasks",
  /**
   * Los filtros de la bandeja viven en la dirección.
   *
   * "Lo vencido de recepción" tiene que ser un enlace que se pega en un chat,
   * no una explicación de qué botones apretar.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    inbox?: string;
    status?: string;
    type?: string;
    queue?: string;
    assignee?: string;
    patientId?: string;
  } =>
    Object.fromEntries(
      ["inbox", "status", "type", "queue", "assignee", "patientId"]
        .map((key) => [key, search[key]])
        .filter(([, value]) => typeof value === "string" && value),
    ),
  component: lazyRouteComponent(
    () => import("./routes/tasks.tsx"),
    "TasksPage",
  ),
});

const referralsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/referrals",
  component: lazyRouteComponent(
    () => import("./routes/referrals.tsx"),
    "ReferralsPage",
  ),
});

const clinicalFormsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/clinical-forms",
  validateSearch: (
    search: Record<string, unknown>,
  ): { view?: "templates" | "responses" } =>
    search.view === "templates" ? { view: "templates" } : {},
  component: lazyRouteComponent(
    () => import("./routes/clinical-forms.tsx"),
    "ClinicalFormsPage",
  ),
});

const prescriptionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/prescriptions",
  component: lazyRouteComponent(
    () => import("./routes/prescriptions.tsx"),
    "PrescriptionsPage",
  ),
});

const prescriptionAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/receta/$token",
  component: lazyRouteComponent(
    () => import("./routes/prescription-access.tsx"),
    "PrescriptionAccessPage",
  ),
});

const appointmentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/appointments",
  validateSearch: (search: Record<string, unknown>): { action?: "new" } =>
    search.action === "new" ? { action: "new" } : {},
  component: lazyRouteComponent(
    () => import("./routes/appointments.tsx"),
    "AppointmentsPage",
  ),
});
const organizationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organization",
  component: lazyRouteComponent(
    () => import("./routes/organization.tsx"),
    "OrganizationPage",
  ),
});
const organizationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organizations",
  validateSearch: (search: Record<string, unknown>): { action?: "new" } =>
    search.action === "new" ? { action: "new" } : {},
  component: lazyRouteComponent(
    () => import("./routes/organizations.tsx"),
    "OrganizationsPage",
  ),
});
const organizationProfileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organization/profile",
  component: lazyRouteComponent(
    () => import("./routes/organization.tsx"),
    "OrganizationProfilePage",
  ),
});
const organizationLocationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organization/locations",
  component: lazyRouteComponent(
    () => import("./routes/organization.tsx"),
    "OrganizationLocationsPage",
  ),
});
const organizationProfessionalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organization/professionals",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    view?: "team" | "availability" | "booking" | "site";
    panel?: "weekly" | "exceptions";
    professional?: string;
  } => ({
    ...(["availability", "booking", "site"].includes(String(search.view))
      ? { view: search.view as "availability" | "booking" | "site" }
      : {}),
    ...(search.panel === "exceptions" ? { panel: "exceptions" as const } : {}),
    ...(typeof search.professional === "string" && search.professional
      ? { professional: search.professional }
      : {}),
  }),
  component: lazyRouteComponent(
    () => import("./routes/organization.tsx"),
    "OrganizationProfessionalsPage",
  ),
});
const organizationCalendarsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organization/calendars",
  component: lazyRouteComponent(
    () => import("./routes/organization.tsx"),
    "OrganizationCalendarsPage",
  ),
  /**
   * Con qué vuelve el proveedor de calendario después de pedir permiso.
   *
   * Los tres los pone la API al redirigir; nadie los escribe a mano. Se
   * declaran acá para que el router no los descarte, que es lo que pasaba
   * antes: la pantalla no tenía forma de saber que venía de autorizar.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    calendar?: "connected" | "denied";
    professional?: string;
    connection?: string;
  } => ({
    ...(search.calendar === "connected" || search.calendar === "denied"
      ? { calendar: search.calendar }
      : {}),
    ...(typeof search.professional === "string" && search.professional
      ? { professional: search.professional }
      : {}),
    ...(typeof search.connection === "string" && search.connection
      ? { connection: search.connection }
      : {}),
  }),
});
const organizationAccessRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organization/access",
  component: lazyRouteComponent(
    () => import("./routes/organization.tsx"),
    "OrganizationAccessPage",
  ),
});
const organizationBillingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organization/billing",
  component: lazyRouteComponent(
    () => import("./routes/organization.tsx"),
    "OrganizationBillingPage",
  ),
});
const organizationDataRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organization/data",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    view?: "retention" | "security" | "communications" | "audit" | "danger";
  } =>
    ["security", "communications", "audit", "danger"].includes(
      String(search.view),
    )
      ? {
          view: search.view as
            "security" | "communications" | "audit" | "danger",
        }
      : {},
  component: lazyRouteComponent(
    () => import("./routes/organization.tsx"),
    "OrganizationDataPage",
  ),
});
const profileSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/profile",
  component: lazyRouteComponent(
    () => import("./routes/settings.tsx"),
    "ProfileSettingsPage",
  ),
});
const securitySettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/security",
  component: lazyRouteComponent(
    () => import("./routes/settings.tsx"),
    "SecuritySettingsPage",
  ),
});
const billingSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/billing",
  beforeLoad: () => {
    throw redirect({ to: "/organization/billing" });
  },
});
const inventoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory",
  /**
   * `?item=` señala a qué insumo llegar.
   *
   * Va en la dirección y no en un estado en memoria para que el aviso del
   * tablero pueda apuntar a una tarjeta concreta, y para que ese enlace se
   * pueda compartir o volver a abrir.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): { item?: string; action?: "new"; view?: "treatments" } => ({
    ...(typeof search.item === "string" && search.item
      ? { item: search.item }
      : {}),
    ...(search.action === "new" ? { action: "new" } : {}),
    // `view=treatments` no lo usa la pantalla: sobrevive sólo para que
    // `beforeLoad` lo reconozca y redirija. Sin declararlo acá, la validación
    // lo descarta antes y el enlace viejo abriría el stock sin explicación.
    ...(search.view === "treatments" ? { view: "treatments" as const } : {}),
  }),
  /**
   * Los tratamientos se fueron a su propia pantalla y este enlace sigue
   * llevando ahí.
   *
   * `?view=treatments` era la pestaña del catálogo dentro de Inventario.
   * Quedó compartida en chats y guardada en marcadores, y una dirección que
   * de golpe muestra el stock se lee como que el catálogo desapareció.
   */
  beforeLoad: ({ search }) => {
    if ((search as { view?: string }).view === "treatments")
      throw redirect({ to: "/treatments", replace: true });
  },
  component: lazyRouteComponent(
    () => import("./routes/operations.tsx"),
    "InventoryPage",
  ),
});
const treatmentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/treatments",
  component: lazyRouteComponent(
    () => import("./routes/treatments.tsx"),
    "TreatmentsPage",
  ),
});
const crmRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/crm",
  component: lazyRouteComponent(
    () => import("./routes/operations.tsx"),
    "CrmPage",
  ),
});
const financesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/finances",
  validateSearch: (
    search: Record<string, unknown>,
  ): { view?: "summary" | "campaigns" | "ledger" } =>
    search.view === "campaigns" || search.view === "ledger"
      ? { view: search.view }
      : {},
  component: lazyRouteComponent(
    () => import("./routes/operations.tsx"),
    "FinancesPage",
  ),
});
const financeCategoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/finances/categories",
  component: lazyRouteComponent(
    () => import("./routes/operations.tsx"),
    "FinanceCategoriesPage",
  ),
});
const metricsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/metrics",
  validateSearch: (
    search: Record<string, unknown>,
  ): { view?: "schedule" | "patients" } =>
    search.view === "patients" ? { view: "patients" } : {},
  component: lazyRouteComponent(
    () => import("./routes/metrics.tsx"),
    "MetricsPage",
  ),
});
const professionalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/professionals",
  validateSearch: (
    search: Record<string, unknown>,
  ): { view?: "team" | "preconsultations" | "capacity" } =>
    search.view === "preconsultations" || search.view === "capacity"
      ? { view: search.view }
      : {},
  component: lazyRouteComponent(
    () => import("./routes/professionals.tsx"),
    "ProfessionalsPage",
  ),
});
const professionalDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/professionals/$professionalId",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    view?:
      | "summary"
      | "schedule"
      | "activity"
      | "metrics"
      | "payouts"
      | "credentials"
      | "settings";
  } =>
    [
      "schedule",
      "activity",
      "metrics",
      "payouts",
      "credentials",
      "settings",
    ].includes(String(search.view))
      ? { view: search.view as "schedule" }
      : {},
  component: lazyRouteComponent(
    () => import("./routes/professionals.tsx"),
    "ProfessionalDetailPage",
  ),
});
const surveysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/surveys",
  validateSearch: (
    search: Record<string, unknown>,
  ): { view?: "summary" | "responses" | "configuration" } =>
    search.view === "responses" || search.view === "configuration"
      ? { view: search.view }
      : {},
  component: lazyRouteComponent(
    () => import("./routes/surveys.tsx"),
    "SurveysPage",
  ),
});
const marketingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/marketing",
  component: lazyRouteComponent(
    () => import("./routes/marketing.tsx"),
    "MarketingPage",
  ),
});

const checkInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/check-in/$token",
  component: lazyRouteComponent(
    () => import("./routes/check-in.tsx"),
    "CheckInPage",
  ),
  pendingComponent: FullScreenLoader,
});

const waitlistOfferRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lista-espera/$token",
  component: lazyRouteComponent(
    () => import("./routes/waitlist-offer.tsx"),
    "WaitlistOfferPage",
  ),
  pendingComponent: FullScreenLoader,
});

const treatmentBudgetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/presupuesto/$token",
  component: lazyRouteComponent(
    () => import("./routes/treatment-budget.tsx"),
    "TreatmentBudgetPage",
  ),
  pendingComponent: FullScreenLoader,
});

const referralAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/derivacion/$token",
  component: lazyRouteComponent(
    () => import("./routes/referral-access.tsx"),
    "ReferralAccessPage",
  ),
  pendingComponent: FullScreenLoader,
});

const sedesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sedes",
  beforeLoad: () => {
    throw redirect({ to: "/organization/locations" });
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  patientLoginRoute,
  portalInviteRoute,
  checkInRoute,
  waitlistOfferRoute,
  treatmentBudgetRoute,
  referralAccessRoute,
  prescriptionAccessRoute,
  signupRoute,
  resetPasswordRoute,
  statusRoute,
  onboardingRoute,
  portalRoute.addChildren([
    portalHomeRoute,
    portalAppointmentsRoute,
    portalOrdersRoute,
    portalMedicationsRoute,
    portalInstructionsRoute,
    portalInstructionRoute,
    portalWaitingRoomRoute,
  ]),
  appRoute.addChildren([
    overviewRoute,
    patientsRoute,
    patientDetailRoute,
    tasksRoute,
    referralsRoute,
    clinicalFormsRoute,
    prescriptionsRoute,
    appointmentsRoute,
    waitingRoomRoute,
    organizationRoute,
    organizationsRoute,
    organizationProfileRoute,
    organizationLocationsRoute,
    organizationProfessionalsRoute,
    organizationCalendarsRoute,
    organizationAccessRoute,
    organizationBillingRoute,
    organizationDataRoute,
    profileSettingsRoute,
    securitySettingsRoute,
    billingSettingsRoute,
    inventoryRoute,
    treatmentsRoute,
    crmRoute,
    financesRoute,
    financeCategoriesRoute,
    sedesRoute,
    metricsRoute,
    professionalsRoute,
    professionalDetailRoute,
    surveysRoute,
    marketingRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  history: window.rondaDesktop ? createHashHistory() : undefined,
  // Lazy route chunks start downloading as soon as the user points at a nav
  // item, so the click itself does not have to wait for the network.
  defaultPreload: "intent",
  defaultPendingComponent: RoutePending,
  defaultPendingMs: 120,
  defaultPendingMinMs: 250,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
